import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

/**
 * 사진 풀스크린 뷰어 — 검은 배경, 스와이프 넘기기, 핀치 줌, 쓸어내려 닫기.
 *
 * **네이티브 스크롤을 쓰지 않는다.** iOS WebKit은 overflow-x 스크롤 컨테이너에서
 * 세로 제스처까지 스크롤 판정으로 삼켜 touchmove를 JS에 주지 않는다(두 번의 실기기
 * 실패로 확인). 그래서 PhotoSwipe류 갤러리처럼 touch-action: none을 걸고
 * 스와이프·핀치·팬·쓸어내리기를 전부 JS 제스처로 직접 굴린다.
 *
 *  - 좌우 스와이프: 트랙 transform 드래그 + 놓을 때 거리/속도로 스냅.
 *  - 핀치 줌(1~4배) + 확대 중 한 손가락 팬 + 더블탭 2.5배 토글.
 *  - 원배율 세로 드래그: 사진이 따라 내려가고 배경이 옅어짐 → 문턱을 넘기면 닫힘.
 * 열려 있는 동안 body 스크롤을 잠근다.
 */

interface Props {
  images: { url: string }[];
  initialIndex: number;
  onClose: () => void;
}

const DISMISS_THRESHOLD = 100; // 이만큼 내리고 놓으면 닫힘(px)
const SWIPE_DIST = 0.22; // 화면 폭 대비 이만큼 끌면 다음 장
const SWIPE_VELOCITY = 0.45; // px/ms — 짧게 휙 넘겨도 다음 장

export function ImageViewer({ images, initialIndex, onClose }: Props) {
  const [index, setIndex] = useState(initialIndex);
  const [width, setWidth] = useState(0);
  const [drag, setDrag] = useState({ x: 0, y: 0 });
  const [animating, setAnimating] = useState(false); // 손을 뗀 뒤 스냅 애니메이션 중

  const rootRef = useRef<HTMLDivElement>(null);
  const indexRef = useRef(initialIndex);
  const scaleRef = useRef(1);
  const panRef = useRef({ x: 0, y: 0 });
  const activeImgRef = useRef<HTMLImageElement | null>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const applyZoom = useCallback(() => {
    const img = activeImgRef.current;
    if (!img) return;
    img.style.transform = `translate(${panRef.current.x}px, ${panRef.current.y}px) scale(${scaleRef.current})`;
  }, []);

  const resetZoom = useCallback(() => {
    if (activeImgRef.current) activeImgRef.current.style.transform = '';
    scaleRef.current = 1;
    panRef.current = { x: 0, y: 0 };
  }, []);

  // 컨테이너 폭 측정 — 슬라이드 오프셋(px) 계산의 기준.
  useLayoutEffect(() => {
    const measure = () => setWidth(rootRef.current?.clientWidth ?? window.innerWidth);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // 문서 스크롤 잠금 + ESC 닫기.
  // iOS는 body만 잠그면 문서(html)가 여전히 고무줄로 움직여 브라우저 새로고침 제스처가
  // 새어 나온다 — html까지 잠그고 overscroll도 끊는다.
  useEffect(() => {
    const html = document.documentElement;
    const prev = {
      bodyOverflow: document.body.style.overflow,
      htmlOverflow: html.style.overflow,
      htmlOverscroll: html.style.overscrollBehavior,
    };
    document.body.style.overflow = 'hidden';
    html.style.overflow = 'hidden';
    html.style.overscrollBehavior = 'none';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev.bodyOverflow;
      html.style.overflow = prev.htmlOverflow;
      html.style.overscrollBehavior = prev.htmlOverscroll;
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  // ── 터치 제스처 전부 여기서 (non-passive) ─────────────────
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const g = {
      mode: 'idle' as 'idle' | 'pinch' | 'pan' | 'swipe' | 'dismiss',
      start: { x: 0, y: 0 },
      startDist: 0,
      startScale: 1,
      startPan: { x: 0, y: 0 },
      lastTap: 0,
      lastX: 0,
      lastT: 0,
      velocity: 0,
      drag: { x: 0, y: 0 },
    };

    const dist = (t: TouchList) =>
      Math.hypot(t[0]!.clientX - t[1]!.clientX, t[0]!.clientY - t[1]!.clientY);
    const mid = (t: TouchList) => ({
      x: (t[0]!.clientX + t[1]!.clientX) / 2,
      y: (t[0]!.clientY + t[1]!.clientY) / 2,
    });

    const settle = (nextIndex: number) => {
      const clamped = Math.max(0, Math.min(images.length - 1, nextIndex));
      if (clamped !== indexRef.current) {
        if (activeImgRef.current) activeImgRef.current.style.transform = '';
        scaleRef.current = 1;
        panRef.current = { x: 0, y: 0 };
        indexRef.current = clamped;
        setIndex(clamped);
      }
      setAnimating(true);
      setDrag({ x: 0, y: 0 });
    };

    const onStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        g.mode = 'pinch';
        g.startDist = dist(e.touches);
        g.startScale = scaleRef.current;
        g.startPan = { ...panRef.current };
        g.start = mid(e.touches);
        return;
      }
      if (e.touches.length !== 1) return;

      // 더블탭 줌 토글.
      const now = performance.now();
      if (now - g.lastTap < 280) {
        if (scaleRef.current > 1) resetZoom();
        else {
          scaleRef.current = 2.5;
          applyZoom();
        }
        g.lastTap = 0;
      } else {
        g.lastTap = now;
      }

      g.start = { x: e.touches[0]!.clientX, y: e.touches[0]!.clientY };
      g.startPan = { ...panRef.current };
      g.lastX = g.start.x;
      g.lastT = now;
      g.velocity = 0;
      g.drag = { x: 0, y: 0 };
      g.mode = scaleRef.current > 1.02 ? 'pan' : 'idle';
      setAnimating(false);
    };

    const onMove = (e: TouchEvent) => {
      e.preventDefault(); // touch-action: none과 함께 — 브라우저 개입(스크롤·페이지 줌) 원천 차단
      if (g.mode === 'pinch' && e.touches.length === 2) {
        const ratio = dist(e.touches) / Math.max(1, g.startDist);
        scaleRef.current = Math.min(4, Math.max(1, g.startScale * ratio));
        const m = mid(e.touches);
        panRef.current = {
          x: g.startPan.x + (m.x - g.start.x),
          y: g.startPan.y + (m.y - g.start.y),
        };
        if (scaleRef.current <= 1.02) panRef.current = { x: 0, y: 0 };
        applyZoom();
        return;
      }
      if (e.touches.length !== 1) return;
      const cx = e.touches[0]!.clientX;
      const cy = e.touches[0]!.clientY;
      const dx = cx - g.start.x;
      const dy = cy - g.start.y;

      // 속도(px/ms) — 휙 넘기기 판정용.
      const now = performance.now();
      if (now > g.lastT) g.velocity = (cx - g.lastX) / (now - g.lastT);
      g.lastX = cx;
      g.lastT = now;

      if (g.mode === 'pan') {
        panRef.current = { x: g.startPan.x + dx, y: g.startPan.y + dy };
        applyZoom();
        return;
      }
      if (g.mode === 'idle' && (Math.abs(dx) > 10 || Math.abs(dy) > 10)) {
        g.mode = Math.abs(dy) > Math.abs(dx) * 1.2 ? 'dismiss' : 'swipe';
      }
      if (g.mode === 'swipe') {
        // 끝 장 너머로는 저항을 준다(고무줄).
        const atEdge =
          (indexRef.current === 0 && dx > 0) ||
          (indexRef.current === images.length - 1 && dx < 0);
        g.drag = { x: atEdge ? dx * 0.35 : dx, y: 0 };
        setDrag(g.drag);
      } else if (g.mode === 'dismiss') {
        g.drag = { x: 0, y: Math.max(0, dy) };
        setDrag(g.drag);
      }
    };

    const onEnd = (e: TouchEvent) => {
      if (g.mode === 'pinch') {
        // 손가락 하나가 남으면 팬으로 이어간다.
        if (e.touches.length === 1) {
          g.start = { x: e.touches[0]!.clientX, y: e.touches[0]!.clientY };
          g.startPan = { ...panRef.current };
          g.mode = scaleRef.current > 1.02 ? 'pan' : 'idle';
          return;
        }
      } else if (g.mode === 'dismiss') {
        if (g.drag.y > DISMISS_THRESHOLD) {
          onCloseRef.current();
          return;
        }
        settle(indexRef.current);
      } else if (g.mode === 'swipe') {
        const w = rootRef.current?.clientWidth ?? window.innerWidth;
        let next = indexRef.current;
        if (g.drag.x < -w * SWIPE_DIST || g.velocity < -SWIPE_VELOCITY) next += 1;
        else if (g.drag.x > w * SWIPE_DIST || g.velocity > SWIPE_VELOCITY) next -= 1;
        settle(next);
      }
      g.mode = 'idle';
      g.drag = { x: 0, y: 0 };
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [applyZoom, resetZoom, images.length]);

  // 데스크톱 키보드 좌우 이동(보너스 — 터치 없는 환경).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') setIndex((i) => Math.min(images.length - 1, i + 1));
      if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [images.length]);
  useEffect(() => {
    indexRef.current = index;
  }, [index]);

  const offsetX = -index * width + drag.x;

  return (
    <div
      ref={rootRef}
      className="fixed inset-0 z-[80] overflow-hidden"
      role="dialog"
      aria-label="사진 크게 보기"
      // 쓸어내리는 만큼 배경이 옅어진다 — "놓으면 닫힌다"는 예고.
      style={{
        backgroundColor: `rgb(0 0 0 / ${Math.max(0.35, 1 - drag.y / 400)})`,
        touchAction: 'none',
      }}
    >
      {/* 슬라이드 트랙 — 드래그를 따라 움직이고, 놓으면 스냅. */}
      <div
        className={`flex h-full ${animating ? 'transition-transform duration-300 ease-(--ease-out-soft)' : ''}`}
        style={{
          width: width * images.length || undefined,
          transform: `translate(${offsetX}px, ${drag.y}px)`,
        }}
        onTransitionEnd={() => setAnimating(false)}
      >
        {images.map((img, i) => (
          <div
            key={img.url}
            className="flex h-full items-center justify-center"
            style={{ width: width || '100%' }}
            // 배경(이미지 밖) 탭으로 닫기 — 이미지 자체 탭은 더블탭 줌과 겹쳐 두지 않는다.
            onClick={(e) => {
              if (e.target === e.currentTarget) onClose();
            }}
          >
            <img
              ref={i === index ? activeImgRef : undefined}
              src={img.url}
              alt=""
              draggable={false}
              className="max-h-full max-w-full select-none object-contain"
            />
          </div>
        ))}
      </div>

      {/* 상단 크롬 — 카운터와 닫기. 검은 배경 위 고정 흰색(다크 모드 무관). */}
      <div className="pointer-events-none absolute inset-x-0 top-0 flex items-center justify-between p-4 pt-[max(env(safe-area-inset-top),16px)]">
        <span className="rounded-full bg-black/50 px-3 py-1 text-[13px] font-medium tabular-nums text-white">
          {index + 1} / {images.length}
        </span>
        <button
          type="button"
          aria-label="닫기"
          onClick={onClose}
          className="pointer-events-auto flex size-9 items-center justify-center rounded-full bg-black/50 text-[16px] text-white transition hover:bg-black/70"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
